import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { fetchWithTimeout } from '@amzn/aac-chrome-internal-api';
import { selectFooterState } from './store/slice';
import {
  selectIsForceDesktopSite,
  selectIsMobileWeb,
  selectIsMobileWebView,
} from 'src/app/slice';
import {
  DEFAULT_ACCEPT_HEADER,
  REQUEST_TIMEOUT,
} from 'src/commons/utils/async-request/constants';
import { logger } from 'src/commons/utils/logger';
import { FooterViewSwitcherPublisher } from 'src/commons/utils/metrics';
import {
  Components,
  FOOTER_FORCE_DESKTOP_ENDPOINT,
  FOOTER_FORCE_MOBILE_ENDPOINT,
} from 'src/commons/constants';
import didElementLoad from 'src/commons/utils/didElementLoad';
import {
  useCTCIMetrics,
  useLogWeblab,
  useLogStormLayoutsUsage,
} from 'src/commons/hooks';
import FooterComponent from './components/Footer';
import { markComponentReady } from 'src/commons/utils/logWithTakt';
import { FooterBundleProvider } from 'src/providers/PantherLocalizationProvider/PantherLocalizationProvider';
import publishNavLoadDuration from './publishNavLoadDuration';
import { loadHmhDeferred } from '../helpmehere/loadHmhBundleScript';
import useLogThemeMode from 'src/commons/hooks/useLogThemeMode';

export const Footer: React.VFC = () => {
  const footerStates = useSelector(selectFooterState);
  const isMobile = useSelector(selectIsMobileWeb);
  const isForceDesktop = useSelector(selectIsForceDesktopSite);
  const isMobileWebView = useSelector(selectIsMobileWebView);

  useCTCIMetrics({ isMobile: isMobile && !isForceDesktop });
  useLogThemeMode();
  useLogStormLayoutsUsage();

  const toggleView = async () => {
    const refName = window?.location?.href || null;
    const postURL = !isForceDesktop
      ? `${FOOTER_FORCE_DESKTOP_ENDPOINT}${refName}`
      : `${FOOTER_FORCE_MOBILE_ENDPOINT}${refName}`;

    try {
      /*
       * fetchWithTimeout directly, not async-request's `request`: this call has
       * never published the per-endpoint Ajax series, and its own
       * FooterViewSwitcher counters are the ones the dashboard reads.
       *
       * GET, not POST: the variable is named postURL but axios was called
       * without a method, so this has always been a GET.
       *
       * `credentials: 'same-origin'` reproduces what axios did. The endpoint is
       * relative, so the request is same-origin, and XHR sends cookies on a
       * same-origin request whatever `withCredentials` says -- the flag only
       * governs cross-origin. fetch's 'omit' would be the literal reading of
       * `withCredentials: false` and would strip the session cookie the
       * view-switch needs.
       *
       * `responseType: 'none'`: the body was never read, only tested for
       * truthiness, and an axios response object is always truthy. A resolved
       * call is the success signal.
       */
      await fetchWithTimeout(postURL, REQUEST_TIMEOUT, {
        credentials: 'same-origin',
        cacheBust: false,
        responseType: 'none',
        headers: { Accept: DEFAULT_ACCEPT_HEADER },
      });

      FooterViewSwitcherPublisher.publishCounterMonitor('success', 1);
      window.location.reload();
    } catch (error) {
      logger.error(
        `Error occurred while switching to ${
          isForceDesktop ? 'mobile' : 'desktop'
        } view in AACChrome, error: ${error}`,
      );
      FooterViewSwitcherPublisher.publishCounterMonitor('error', 1);
    }
  };

  useLogWeblab();

  useEffect(() => {
    didElementLoad('footer');
    markComponentReady(Components.FOOTER);
    publishNavLoadDuration();
  }, []);

  useEffect(() => loadHmhDeferred() || undefined, []);

  return (
    <FooterBundleProvider>
      <FooterComponent
        {...footerStates}
        isMobile={isMobile}
        isMobileWebView={isMobileWebView} //  seller App
        isForceDesktop={isForceDesktop}
        toggleView={toggleView}
      />
    </FooterBundleProvider>
  );
};
